/**
 * How you actually get out — as a LIST, not a label.
 *
 * `withdrawalMode` names the SHAPE of an exit, and for half our modes that
 * shape is two different things at once. `fee-or-queued` means "pay a fee and
 * leave now, OR wait and leave cheaply", and the two legs disagree on every
 * number a holder cares about: the fee, the wait, the minimum size, and how
 * much can go through at all. Collapsing that into one mode string plus one
 * `withdrawalCooldownSeconds` plus one `withdrawFeeBps` forces every consumer
 * to re-derive the split — and they get it wrong in opposite directions,
 * because the fee belongs to one leg and the cooldown to the other.
 *
 * It is not a Treehouse problem. `fee-or-queued` is shared by Puffer pufETH,
 * b14g dualCORE, PrimeStaking psXDC, Native wNLP and the Treehouse tAssets;
 * `instant-or-queued` by rETH, weETH, beHYPE and more. So the routes are
 * derived generically from what a row already publishes, and a provider that
 * knows better — because it reads its own exit modules — overrides them with
 * live values.
 *
 * The invariant: **every row publishes at least one route**, and the union of
 * the routes is the whole truth about leaving.
 */

/** What settling on this route costs you in TIME. */
export type VaultExitRouteKind =
  /** Same block. May still be capped by `capacity`. */
  | 'instant'
  /** Request now, claim later — `waitSeconds` if the protocol pins one. */
  | 'queued'
  /** No redemption: you sell the instrument to somebody. */
  | 'market'

/**
 * One way out of a position. Amounts are RAW integer strings in the
 * **underlying** asset's units, matching `liquidity` / `totalAssets` on the
 * row; the `*Formatted` twins are the same numbers scaled by its decimals.
 */
export interface VaultExitRoute {
  /** Stable slug, unique within the row. Consumers may key on it. */
  id: string
  kind: VaultExitRouteKind
  /** Short human label — `Instant`, `7-day queue`, `Sell on the market`. */
  label: string
  settlement: 'sync' | 'async' | 'market'
  /**
   * Cost of taking THIS route, in basis points of the payout. `0` is a real
   * answer meaning free; `undefined` means the protocol publishes no fee for
   * it (a market route's cost is price impact, not a fee).
   */
  feeBps?: number
  /**
   * `true` when this leg definitely charges a fee but the row does not
   * publish its size — the mode itself guarantees the fee exists
   * (`fee-or-queued` means the instant leg is the paying one), while the
   * number is only known to providers that read their own dials.
   *
   * Without it an absent `feeBps` renders as "leave instantly", which reads
   * as FREE — the exact failure the term-sheet rules call out for `fees: []`.
   */
  feeUnknown?: boolean
  /** Seconds between requesting and being able to claim. Absent when the wait
   *  is a queue with no pinned duration (Lido's validator exit). */
  waitSeconds?: number
  /**
   * Smallest size this route accepts, RAW underlying. The field that decides
   * whether a cheap leg is reachable at all: Treehouse's 5 bps queue has a
   * 50 wstETH floor, so almost every holder can only take its 0.5 % instant
   * leg. Absent ⇒ no minimum.
   */
  minAmount?: string
  minAmountFormatted?: number
  /**
   * Most that can settle through this route RIGHT NOW, RAW underlying.
   * Absent ⇒ unbounded (a queue is not capped by inventory). A present `'0'`
   * means the route exists but cannot serve anything this block.
   */
  capacity?: string
  capacityFormatted?: number
  capacityUsd?: number
  /** `true` when the entrypoint pays `msg.sender` only, i.e. it cannot be
   *  composed to credit somebody else. */
  selfOnly?: boolean
  /** One sentence, when the route needs one to be honest. */
  description?: string
}

/** Row-level inputs the generic derivation reads. All optional — a row that
 *  publishes none still gets one route from its mode alone. */
export interface ExitRouteInputs {
  withdrawalMode?: string
  /** Fee on the INSTANT leg, bps. By convention across both registries this
   *  field is the fee-paying leg's, never the queue's. */
  withdrawFeeBps?: number
  withdrawalCooldownSeconds?: number
  /** Same-block withdrawable underlying, RAW. */
  liquidity?: string
  liquidityFormatted?: number
  liquidityUsd?: number
  /**
   * Smallest amount the protocol's own exit accepts, RAW underlying. Attaches
   * to the QUEUED leg, which is the one every mode that has a floor puts it on.
   */
  exitMinAmount?: string
  exitMinAmountFormatted?: number
  /**
   * The protocol's mint AND redeem are permissioned while the share token
   * trades freely — so for OUR users the market is not a bonus route, it is
   * the only one there is.
   */
  secondaryMarketOnly?: boolean
}

const asyncLabel = (secs?: number): string => {
  if (!secs || secs <= 0) return 'Queued'
  const d = secs / 86_400
  if (d >= 1) return `${Number.isInteger(d) ? d : d.toFixed(1)}-day queue`
  const h = secs / 3_600
  return h >= 1
    ? `${Number.isInteger(h) ? h : h.toFixed(1)}-hour queue`
    : `${Math.round(secs / 60)}-minute queue`
}

const instantRoute = (i: ExitRouteInputs, feeBps?: number): VaultExitRoute => ({
  id: 'instant',
  kind: 'instant',
  label: feeBps ? 'Instant (fee)' : 'Instant',
  settlement: 'sync',
  feeBps,
  waitSeconds: 0,
  capacity: i.liquidity,
  capacityFormatted: i.liquidityFormatted,
  capacityUsd: i.liquidityUsd,
})

const queuedRoute = (i: ExitRouteInputs): VaultExitRoute => ({
  id: 'queued',
  kind: 'queued',
  label: asyncLabel(i.withdrawalCooldownSeconds),
  settlement: 'async',
  waitSeconds: i.withdrawalCooldownSeconds,
  ...(i.exitMinAmount !== undefined
    ? {
        minAmount: i.exitMinAmount,
        minAmountFormatted: i.exitMinAmountFormatted,
      }
    : {}),
})

/**
 * Derive the route list from a row's published exit fields.
 *
 * Deliberately conservative: it never invents a fee, a wait or a minimum the
 * row does not carry — an absent number stays absent rather than defaulting
 * to a comfortable `0`. The one thing it does assert is the SPLIT, because
 * that is what the mode name already promises.
 */
const marketRoute = (): VaultExitRoute => ({
  id: 'market',
  kind: 'market',
  label: 'Sell on the market',
  settlement: 'market',
  description:
    "The protocol's own mint and redeem are permissioned, so for a holder without that permission this is the route: sell the share token at whatever the book bids, with price impact that grows with size. It fills at the MARKET price, which is not the row's `exchangeRate` — that stays the NAV — so the leg must be built with a slippage bound.",
})

export const deriveExitRoutes = (i: ExitRouteInputs): VaultExitRoute[] => {
  const routes = deriveProtocolExitRoutes(i)
  // Appended, never substituted: the protocol's own exit still exists and is
  // still the right description of what a PERMISSIONED holder does. What
  // `secondaryMarketOnly` adds is the route OUR user actually has, and a row
  // publishing only the queued leg was describing somebody else's exit — Re's
  // two rows have been doing exactly that.
  return i.secondaryMarketOnly ? [...routes, marketRoute()] : routes
}

const deriveProtocolExitRoutes = (i: ExitRouteInputs): VaultExitRoute[] => {
  switch (i.withdrawalMode) {
    // The instant leg pays the fee; the queue pays at par. That is the
    // convention `SavingsVault.withdrawFeeBps` already documents — and
    // Treehouse is the counter-example, which is why its reader overrides
    // this rather than being described by it.
    case 'fee-or-queued':
      return [
        {
          ...instantRoute(i, i.withdrawFeeBps),
          // The mode's whole meaning is that THIS leg costs something. Say so
          // even when the size is unknown: an absent `feeBps` renders as
          // "leave instantly", which reads as free — the same failure the
          // term-sheet rules call out for `fees: []`.
          ...(i.withdrawFeeBps == null
            ? { feeUnknown: true, label: 'Instant (fee)' }
            : {}),
        },
        { ...queuedRoute(i), feeBps: 0 },
      ]
    // Same split with no fee on either leg — the instant leg is bounded by
    // inventory instead.
    case 'instant-or-queued':
      return [instantRoute(i, i.withdrawFeeBps ?? 0), queuedRoute(i)]
    case 'instant':
      // Uncapped by construction: `capacity` is left off rather than set to
      // the liquidity reading, which would read as a limit that is not one.
      return [
        {
          id: 'instant',
          kind: 'instant',
          label: 'Instant',
          settlement: 'sync',
          feeBps: i.withdrawFeeBps ?? 0,
          waitSeconds: 0,
        },
      ]
    case 'instant-capped':
      return [instantRoute(i, i.withdrawFeeBps ?? 0)]
    case 'fixed-cooldown':
    case 'queued':
    case 'request-based':
      return [{ ...queuedRoute(i), feeBps: i.withdrawFeeBps }]
    case 'off-chain':
      return [
        {
          id: 'off-chain',
          kind: 'queued',
          label: 'Off-chain redemption',
          settlement: 'async',
          waitSeconds: i.withdrawalCooldownSeconds,
          description:
            'Redemption is handled by the issuer off-chain; there is no on-chain exit to call.',
        },
      ]
    case 'dex-only':
    case 'market-sale':
      return [
        {
          id: 'market',
          kind: 'market',
          label: 'Sell on the market',
          settlement: 'market',
          description:
            'There is no redemption — you leave by selling the token, at whatever price is bid and with price impact that grows with size.',
        },
      ]
    default:
      return []
  }
}
